import { FunctionComponent, useState } from 'react';
import cn from 'classnames';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, Section, SectionHeading } from '../components';
import {
  EntityType,
  FormSpec,
  LegalEntity,
  Person,
  Requirement,
} from '../types';
import { cleanIntakeDates } from '../utils/intake';
import { getRegionNameByCode } from '../utils/format';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus, IApiData } from '../utils/types';
import { Renderer, formSpecToRendererConfig } from '../components/forms';
import ScForeignQualificationSpec from '../components/forms/specs/sc_foreign_qualification_spec.json';
import TxForeignQualificationSpec from '../components/forms/specs/tx_foreign_qualification_spec.json';
import DefaultCorpForeignQualificationSpec from '../components/forms/specs/default_corp_foreign_qualification_spec.json';
import DefaultLLCForeignQualificationSpec from '../components/forms/specs/default_llc_foreign_qualification_spec.json';
import { useScrollIntoView } from '../hooks';
import { FormError } from '../components/forms/FormError';
import { useRequirementFee } from '../hooks/useRequirementFee';
import { RequirementFeeText } from '../components/RequirementFeeText';

export const StateSpecMappingNew: Record<string, FormSpec> = {
  sc: ScForeignQualificationSpec,
  tx: TxForeignQualificationSpec,
};

type ForeignQualificationProps = {
  locationId: string;
  requirement: Requirement;
  legalEntity: LegalEntity;
  peopleData: Person[];
  onEnterLater: () => void;
  onSubmitSuccess: () => void;
  isFullWidth?: boolean;
  scrollOnMount?: boolean;
};

export const ForeignQualificationNew: FunctionComponent<
  ForeignQualificationProps
> = ({
  locationId,
  requirement,
  legalEntity,
  peopleData,
  onEnterLater,
  onSubmitSuccess,
  isFullWidth,
  scrollOnMount,
}) => {
  const fee = useRequirementFee(requirement);
  const [scrollIntoViewRef] = useScrollIntoView(scrollOnMount);
  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);
  const isSpecSupported =
    Object.keys(StateSpecMappingNew).indexOf(locationId) > -1;

  const formMethods = useForm();
  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit = (data: any) => {
    setHasSubmitError(false);
    if (errors.isNotEmpty) {
      return;
    }

    const cleanedData = cleanIntakeDates(data);
    const payload = isSpecSupported
      ? { data: cleanedData }
      : { data: { default: cleanedData } };

    fetchApi({
      // realized this might invalidate the submitUrl field on the spec?
      // todo: decide which approach is better...
      url: `/api/legal_entity/locations/${locationId}/foreign_qualification`,
      method: 'POST',
      body: {
        data: payload,
        legal_entity_requirement_id: requirement.id,
      },
    }).then(({ status: status1 }: IApiData) => {
      if (status1 === ApiStatus.Error) {
        setHasSubmitError(true);
      } else {
        onSubmitSuccess();
      }
    });
  };

  // Now that we support LLC and C Corp we need to load specialized
  // versions based on the entity type
  let loadedSpec;
  if (legalEntity.entity_type === EntityType.CCorp) {
    loadedSpec = isSpecSupported
      ? StateSpecMappingNew[locationId]
      : DefaultCorpForeignQualificationSpec;
  } else if (legalEntity.entity_type === EntityType.LLC) {
    loadedSpec = DefaultLLCForeignQualificationSpec;
  } else {
    // Final fallback
    loadedSpec = DefaultCorpForeignQualificationSpec;
  }

  return (
    <Section className="flex flex-col items-center" ref={scrollIntoViewRef}>
      <SectionHeading
        className={cn('pb-4', {
          'w-full': isFullWidth,
          'w-96': !isFullWidth,
        })}
        text={
          requirement.title ||
          getRegionNameByCode(locationId) + ' Foreign Qualification'
        }
      />
      <FormProvider {...formMethods}>
        <form
          className={cn('my-8 ', {
            'w-full': isFullWidth,
            'w-96': !isFullWidth,
          })}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Renderer
            config={formSpecToRendererConfig(loadedSpec)}
            errors={errors}
            peopleData={peopleData}
          />
          {fee && (
            <div className="my-4">
              <RequirementFeeText fee={fee} />
            </div>
          )}
          <div className="mt-4 flex">
            <div className="flex gap-x-4">
              <Button
                type="button"
                variant="grayTertiary"
                size="large"
                isFullWidth
                onClick={onEnterLater}
              >
                Enter later
              </Button>
              <Button type="submit" isFullWidth size="large">
                Submit
              </Button>
            </div>
            {hasSubmitError && <FormError />}
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};
